import React, { useContext, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
// import { ProgressBar } from "react-bootstrap"; // COMMENTED OUT - Not needed since progress bar is removed
import TemplateDataContext from "../../../Contexts/TemplateData.context";
import { TemplateObject } from "../../../Models/TemplateObject";
import DropDownNavbar from "./DropDownNavbar";
import NavBarItem from "./NavBarItem";
import MissingInfoModal from "../TemplateDesign/MissingInfoModal";
import FormDataContext from "../../../Contexts/FormData.context";
import { isStepCompleted, hasIncompleteFields } from "../../../Util/StepCompletionHelper";
import { jsonKeyMapping } from "../../../Config/Constants";

type Props = {
  uri: string;
  // progressIndicator: number; // COMMENTED OUT - Not needed since progress bar is removed
  show: boolean;
  isHideSteps: string[];
};

const Nav = ({ uri, /* progressIndicator, */ show, isHideSteps }: Props) => {
  const { state } = useContext(TemplateDataContext);
  const { state: formState } = useContext(FormDataContext);
  const formData = formState?.formData || {};
  const { activeTemplateGroupBy, activeTemplateSteps } = state;
  const [navbarSteps, setNavbarSteps] = useState<object[]>([]);
  const { stepName } = useParams();
  const [missingInfoModalOpen, setMissingInfoModalOpen] = useState(false);
  const [initialMissingStepSlug, setInitialMissingStepSlug] = useState<string | undefined>(undefined);
  let isVisited = false;

  useEffect(() => {
    const navSteps: object[] = [];
    activeTemplateGroupBy.length > 0 &&
      activeTemplateGroupBy.forEach((item: any) => {
        // normalize hide list (prop may be undefined)
        const hideList: string[] = Array.isArray(isHideSteps)
          ? isHideSteps
          : [];
        if (!hideList.includes(item.slugify)) {
          // Show if it has a real formGroup (actual form steps) OR if it's an explicit
          // navigation/review step (e.g., has previous/submit flags or a description)
          const hasFormGroup =
            item[jsonKeyMapping.FORM_GROUP] &&
            Array.isArray(item[jsonKeyMapping.FORM_GROUP]) &&
            item[jsonKeyMapping.FORM_GROUP].length > 0;
          const isNavOnlyStep =
            item[jsonKeyMapping.HAS_PREVIOUS] ||
            item[jsonKeyMapping.HAS_SUBMIT] ||
            item[jsonKeyMapping.DESCRIPTION];

          if (hasFormGroup || isNavOnlyStep) {
            navSteps.push(item);
          }
        }
      });
    setNavbarSteps(navSteps);
  }, [state]);

  // Determine overall completion of steps that actually have fields
  const stepsWithFields = useMemo(() => {
    return (activeTemplateSteps || []).filter(
      (s: any) =>
        s &&
        s[jsonKeyMapping.FORM_GROUP] &&
        Array.isArray(s[jsonKeyMapping.FORM_GROUP]) &&
        s[jsonKeyMapping.FORM_GROUP].length > 0 &&
        s[jsonKeyMapping.SLUGIFY] !== "review-submit"
    );
  }, [activeTemplateSteps]);

  const allStepsCompleted = useMemo(() => {
    if (!stepsWithFields || stepsWithFields.length === 0) return false;
    return stepsWithFields.every((s: any) => isStepCompleted(s, formData));
  }, [stepsWithFields, formData]);

  // Intercept navigation only when viewing the review summary and there is missing info
  const shouldInterceptNavigation = stepName === 'review-submit' && !allStepsCompleted;

  const handleAttemptNavigate = (step: TemplateObject, defaultNavigate: () => boolean) => {
    const stepShowsIncompleteIcon = hasIncompleteFields(step, formData) && !isStepCompleted(step, formData);
    if (shouldInterceptNavigation && stepShowsIncompleteIcon) {
      setInitialMissingStepSlug(step[jsonKeyMapping.SLUGIFY]);
      setMissingInfoModalOpen(true);
      return true; // intercepted
    }
    return defaultNavigate(); // not intercepted; proceed
  };

  return (
    <>
      {missingInfoModalOpen && (
        <MissingInfoModal
          open={missingInfoModalOpen}
          onClose={() => {
            setMissingInfoModalOpen(false);
            setInitialMissingStepSlug(undefined);
          }}
          initialStepSlug={initialMissingStepSlug}
        />
      )}
      {/* COMMENTED OUT - Desktop progress bar to remove layout space */}
      {/* <div className="p-20 blue-color progressnone">Progress</div>
      <div className="pe-3 ps-13rem pb-20 blue-color progressnone">
        <ProgressBar
          variant="primary"
          className="progress-indicator"
          now={progressIndicator}
        ></ProgressBar>
        <div className="mt-2">{Math.round(progressIndicator)}%</div>
      </div> */}
      {/* TODO Remove Header in side bar and add Cross icon in place of Toggle Button */}
      <div id="navbarNav" className={show ? "show" : ""}>
        {show ? (
          <>
            {/* COMMENTED OUT - Mobile progress bar to remove layout space */}
            {/* <div className="p-20 blue-color">Progress</div>
            <div className="pe-3 ps-13rem pb-20 blue-color">
              <ProgressBar
                variant="primary"
                className="progress-indicator"
                now={progressIndicator}
              ></ProgressBar>
              <div className="mt-2">{Math.round(progressIndicator)}%</div>
            </div> */}
          </>
        ) : null}
        {/* TODO:CSS : Arrow Icon */}
        <ul className="list-group">
          {navbarSteps.map(
            (step: TemplateObject, stepPositionIndex: number) => {
              if (step.groupName !== undefined) {
                return (
                  <DropDownNavbar
                    uri={uri}
                    groupStepList={step}
                    groupName={step.groupName}
                    key={stepPositionIndex}
                    onAttemptNavigate={handleAttemptNavigate}
                    shouldInterceptNavigation={shouldInterceptNavigation}
                  />
                );
              }

              const isActive = stepName === step[jsonKeyMapping.SLUGIFY];
              /* Find the current step index */
              const stepIndex = activeTemplateSteps.findIndex(
                (step: TemplateObject) =>
                  step[jsonKeyMapping.SLUGIFY] === stepName
              );
              /* compare the current step index with the steps   */
              for (let index = 0; index < activeTemplateSteps.length; index++) {
                if (
                  activeTemplateSteps[index][jsonKeyMapping.SLUGIFY] ===
                  step[jsonKeyMapping.SLUGIFY]
                ) {
                  stepIndex > index ? (isVisited = true) : (isVisited = false);
                }
              }

              return (
                <NavBarItem
                  isVisited={isVisited}
                  isActive={isActive}
                  step={step}
                  uri={uri}
                  key={stepPositionIndex}
                  onAttemptNavigate={handleAttemptNavigate}
                  shouldInterceptNavigation={shouldInterceptNavigation}
                />
              );
            }
          )}
        </ul>
      </div>
    </>
  );
};

export default Nav;
